import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import DatePicker from "react-datepicker";

const DynamicForm = ({ schema, onChange, defaultValues = {} }) => {
  const initialState = {};
  const initialErrors = {};

  // Deep clone defaultValues to avoid reference issues
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));
  const clonedDefaults = deepClone(defaultValues || {});

  // Normalize "data" wrappers for related entities
  const normalizeDataField = (field, fieldKey, defaultValue) => {
    if (!defaultValue || typeof defaultValue !== "object" || !("data" in defaultValue)) {
      return defaultValue;
    }

    if (fieldKey === "course__prerequisite_course") {
      return defaultValue.data.map(item => item.id);
    }

    return defaultValue.data.map(item => {
      if (field.element?.target_id?.type === "entity_autocomplete") {
        const normalized = {
          target_id: item.id,
          title: item.title || "",
          ...(item.meta || {})
        };

        // Remove unwanted keys
        delete normalized.drupal_internal__target_id;
        delete normalized.title;

        // Convert boolean meta fields to string
        Object.entries(normalized).forEach(([k, v]) => {
          if (typeof v === "boolean") {
            normalized[k] = v ? "1" : "0";
          }
        });

        // Cast meta fields to string for select/autocomplete
        Object.entries(field.element || {}).forEach(([subKey, subField]) => {
          if (subKey in normalized && (subField.type === "select" || subField.type === "entity_autocomplete")) {
            normalized[subKey] = normalized[subKey] != null ? String(normalized[subKey]) : "";
          }
        });

        return normalized;
      }

      return item.id;
    });
  };

  // Initialize formData and errors
  Object.entries(schema).forEach(([key, field]) => {
    const isMultiple = field?.cardinality === -1;
    const isCompound = typeof field.element === "object" && Object.keys(field.element).length > 1;

    let defaultValue = clonedDefaults[key];

    // Normalize "data" wrappers
    defaultValue = normalizeDataField(field, key, defaultValue);

    // If no value from backend, use schema's default_value
    if (defaultValue === undefined || defaultValue === null || (isMultiple && defaultValue.length === 0)) {
      if (isCompound) {
        defaultValue = {};
        Object.entries(field.element).forEach(([subKey, subField]) => {
          defaultValue[subKey] = subField.default_value !== undefined && subField.default_value !== null
            ? subField.default_value
            : '';
        });
      } else {
        const element = Object.values(field.element)[0];
        defaultValue = element?.default_value ?? '';
      }
    }

    const convertBoolean = (val) => {
      if (val === 'true') return '1';
      if (val === 'false') return '0';
      return val;
    };

    if (isMultiple) {
      initialState[key] = Array.isArray(defaultValue)
        ? defaultValue.map(convertBoolean)
        : [convertBoolean(defaultValue)];
      initialErrors[key] = new Array(initialState[key].length).fill('');
    } else {
      initialState[key] = convertBoolean(defaultValue);
      initialErrors[key] = '';
    }
  });

  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData]);

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validateField = (field, value) => {
    const isCompound = typeof field.element === 'object' && Object.keys(field.element).length > 1;

    if (isCompound) {
      for (const [subKey, subField] of Object.entries(field.element)) {
        const subValue = value?.[subKey];

        if (subField.required && !subValue) {
          return `Required: ${subKey}`;
        }

        if (subField.type === 'url' && subValue && !isValidUrl(subValue)) {
          return `Invalid URL: ${subKey}`;
        }
      }
    } else {
      const element = Object.values(field.element)[0];

      if (field.required && (!value || value === '')) {
        return 'Required';
      }

      if (element.type === 'url' && value && !isValidUrl(value)) {
        return 'Invalid URL';
      }
    }

    return '';
  };

  const handleChange = (fieldKey, value, index = null, subKey = null) => {

    setFormData(prev => {
      const updated = { ...prev };
      if (index !== null) {
        if (subKey !== null) {
          updated[fieldKey][index] = {
            ...updated[fieldKey][index],
            [subKey]: value
          };
        } else {
          updated[fieldKey][index] = value;
        }
      } else {
        if (subKey !== null) {
          updated[fieldKey] = {
            ...updated[fieldKey],
            [subKey]: value
          };
        } else {
          updated[fieldKey] = value;
        }
      }
      return updated;
    });

    setErrors(prev => {
      const updated = { ...prev };
      const field = schema[fieldKey];
      const isMultiple = field.cardinality === -1;
      const fieldValue = index !== null ? formData[fieldKey][index] : formData[fieldKey];

      const newValue =
        subKey !== null && typeof fieldValue === 'object'
          ? { ...fieldValue, [subKey]: value }
          : value;

      const validationError = validateField(field, newValue);

      if (index !== null) {
        updated[fieldKey][index] = validationError;
      } else {
        updated[fieldKey] = validationError;
      }

      return updated;
    });
  };

  const addField = (fieldKey) => {
    const field = schema[fieldKey];
    const isCompound = typeof field.element === 'object' && Object.keys(field.element).length > 1;

    setFormData(prev => ({
      ...prev,
      [fieldKey]: [...prev[fieldKey], isCompound ? {} : '']
    }));

    setErrors(prev => ({
      ...prev,
      [fieldKey]: [...prev[fieldKey], '']
    }));
  };

  const removeField = (fieldKey, index) => {
    const field = schema[fieldKey];
    const isCompound = typeof field.element === 'object' && Object.keys(field.element).length > 1;

    setFormData(prev => {
      const updated = [...prev[fieldKey]];
      updated.splice(index, 1);
      if (updated.length === 0) {
        updated.push(isCompound ? {} : '');
      }
      return { ...prev, [fieldKey]: updated };
    });

    setErrors(prev => {
      const updated = [...prev[fieldKey]];
      updated.splice(index, 1);
      if (updated.length === 0) {
        updated.push('');
      }
      return { ...prev, [fieldKey]: updated };
    });
  };

  const renderSimpleField = (field, fieldKey, index = null) => {
    const isMultiple = field.cardinality === -1;
    const value = isMultiple ? formData[fieldKey][index] : formData[fieldKey];
    const error = isMultiple ? errors[fieldKey][index] : errors[fieldKey];
    const element = Object.values(field.element)[0];
    const helpText = field.display?.help_text;
    const disabled = Object.entries(element.options || {}).length === 1;
    const fieldRegex = field.display?.field_regex?.find(
      (r) => r.target === fieldKey
    );
    const pattern = fieldRegex?.pattern;

    // Compute default value from schema if formData is empty
    let effectiveValue = value;
    if (
      (effectiveValue === undefined || effectiveValue === null || effectiveValue === '') &&
      element.default_value !== undefined &&
      element.default_value !== null
    ) {
      if (element.type === 'select' && element.options) {
        // Handle default_value as index or key
        if (Array.isArray(element.options)) {
          effectiveValue = element.options[element.default_value] ?? '';
        } else if (typeof element.options === 'object') {
          // If it's an object (grouped options), assume default_value is a key
          effectiveValue = element.default_value;
        }
      } else {
        // For text, number, etc.
        effectiveValue = element.default_value;
      }
    }

    const commonProps = {
      name: fieldKey,
      value: effectiveValue ?? '',
      disabled,
      isInvalid: !!error,
      required: field.required,
      placeholder: element.placeholder ? element.placeholder : element.title,
      pattern: pattern,
      onChange: (e) => handleChange(fieldKey, e.target.value, index),
    };

    switch (element.type) {
      case 'textfield':
        return (
          <>
            <Form.Control type="text" {...commonProps} />
            <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
            {helpText && <small className="form-text text-muted">{helpText}</small>}
          </>
        );

      case 'datetime':
        return (
          <>
            <DatePicker
              selected={effectiveValue ? new Date(effectiveValue) : null}
              onChange={(date) => {
                const formatted = date ? date.toISOString().split('T')[0] : '';
                handleChange(fieldKey, formatted, index);
              }}
              dateFormat="yyyy-MM-dd"
              placeholderText={element.placeholder || element.title}
              className={`form-control ${error ? 'is-invalid' : ''}`}
              popperPlacement="bottom"
            />
            <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
            {helpText && <small className="form-text text-muted">{helpText}</small>}
          </>
        );

      case 'url':
        return (
          <>
            <Form.Control type="url" {...commonProps} />
            <Form.Control.Feedback type="invalid">{error || 'Must be a valid URL'}</Form.Control.Feedback>
            {helpText && <small className="form-text text-muted">{helpText}</small>}
          </>
        );

      case 'textarea':
        return (
          <>
            <Form.Control as="textarea" rows={3} {...commonProps} />
            <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
            {helpText && <small className="form-text text-muted">{helpText}</small>}
          </>
        );

      case 'number':
        return (
          <>
            <Form.Control type="number" {...commonProps} />
            <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
            {helpText && <small className="form-text text-muted">{helpText}</small>}
          </>
        );

      case 'select':
      case 'entity_autocomplete':
        const orderedOptions = Object.entries(element.options || {}).sort(([a], [b]) => {
          if (a === "") return -1;
          if (b === "") return 1;
          return isNaN(a) || isNaN(b) ? a.localeCompare(b) : Number(a) - Number(b);
        });

        return (
          <>
            <Form.Select {...commonProps}>
              {orderedOptions.map(([groupLabel, groupOptions]) => {
                if (typeof groupOptions === 'object') {
                  return (
                    <optgroup key={groupLabel} label={groupLabel}>
                      {Object.entries(groupOptions).map(([val, label]) => (
                        <option key={val} value={val}>{label}</option>
                      ))}
                    </optgroup>
                  );
                }
                return (
                  <option key={groupLabel} value={groupLabel}>
                    {groupOptions}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
            {helpText && <small className="form-text text-muted">{helpText}</small>}
          </>
        );

      default:
        return null;
    }
  };

  const renderCompoundField = (field, fieldKey, index = null) => {
    const isMultiple = field.cardinality === -1;
    const value = isMultiple
      ? formData[fieldKey]?.[index] || {}
      : formData[fieldKey] || {};
    const error = isMultiple
      ? errors[fieldKey]?.[index] || {}
      : errors[fieldKey] || {};
    const helpText = field.display?.help_text;
    const compoundHasLabels = Object.values(field.element).some(
      (subField) => !!subField?.title
    );

    return (
      <div className="d-flex flex-wrap gap-2">
        {Object.entries(field.element).map(([subKey, subField], idx) => {
          // ✅ Use either prefilled value or default
          const hasDefault =
            subField.default_value !== undefined &&
            subField.default_value !== null;
          const subValue =
            value?.[subKey] !== undefined && value?.[subKey] !== null
              ? value[subKey]
              : hasDefault
              ? subField.default_value
              : "";

          const subError = error?.[subKey] || "";
          const placeholder =
            subField.placeholder && subField.placeholder !== ""
              ? subField.placeholder
              : field.title;
          const disabled = Object.entries(subField.options || {}).length === 1;

          const fieldRegex = field.display?.field_regex?.find(
            (r) => r.target === subKey
          );
          const pattern = fieldRegex?.pattern;
          const hasLabel = !!subField?.title;
          const label = hasLabel ? (
            <Form.Label className="mt-1 mb-0 fw-light">
              {subField.title}
              {subField.required ? " *" : ""}
            </Form.Label>
          ) : null;

          const style = idx === 0 ? { minWidth: "250px" } : { minWidth: "200px" };
          const className = idx === 0 ? "flex-md-grow-1" : "";
          const wrapperClassName = [
            className,
            !hasLabel && compoundHasLabels ? "mt-4" : "",
          ].join(" ");

          const commonProps = {
            name: subKey,
            value: subValue,
            disabled,
            isInvalid: !!subError,
            placeholder,
            required: subField.required,
            pattern,
            onChange: (e) => handleChange(fieldKey, e.target.value, index, subKey),
          };

          switch (subField.type) {
            case "textfield":
              return (
                <div key={subKey} className={wrapperClassName} style={style}>
                  {label}
                  <Form.Control type="text" {...commonProps} />
                  <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
                  {helpText && (
                    <small className="form-text text-muted">{helpText}</small>
                  )}
                </div>
              );

            case "datetime":
              return (
                <div key={subKey} className={wrapperClassName} style={style}>
                  {label}
                  <DatePicker
                    selected={subValue ? new Date(subValue) : null}
                    onChange={(date) => {
                      const formatted = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      handleChange(fieldKey, formatted, index, subKey);
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={subField.placeholder || subField.title}
                    className={`form-control ${subError ? "is-invalid" : ""}`}
                    popperPlacement="bottom"
                  />
                  <Form.Control.Feedback type="invalid">{`Please provide a valid ${subField.title}`}</Form.Control.Feedback>
                  {helpText && (
                    <small className="form-text text-muted">{helpText}</small>
                  )}
                </div>
              );

            case "url":
              return (
                <div key={subKey} className={wrapperClassName} style={style}>
                  {label}
                  <Form.Control type="url" {...commonProps} />
                  <Form.Control.Feedback type="invalid">
                    {"Type a valid URL"}
                  </Form.Control.Feedback>
                  {helpText && (
                    <small className="form-text text-muted">{helpText}</small>
                  )}
                </div>
              );

            case "textarea":
              return (
                <div key={subKey} className={wrapperClassName} style={style}>
                  {label}
                  <Form.Control as="textarea" rows={3} {...commonProps} />
                  <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
                  {helpText && (
                    <small className="form-text text-muted">{helpText}</small>
                  )}
                </div>
              );

            case "select":
            case "entity_autocomplete":
              const orderedOptions = Object.entries(subField.options || {}).sort(
                ([a], [b]) => {
                  if (a === "") return -1;
                  if (b === "") return 1;
                  return isNaN(a) || isNaN(b)
                    ? a.localeCompare(b)
                    : Number(a) - Number(b);
                }
              );

              return (
                <div key={subKey} className={wrapperClassName} style={style}>
                  {label}
                  <Form.Select
                    {...commonProps}
                    className={!hasLabel && compoundHasLabels ? "mt-1" : ""}
                  >
                    {orderedOptions.map(([groupLabel, groupOptions]) => {
                      if (typeof groupOptions === "object") {
                        return (
                          <optgroup key={groupLabel} label={groupLabel}>
                            {Object.entries(groupOptions).map(([val, label]) => (
                              <option key={val} value={val}>
                                {label}
                              </option>
                            ))}
                          </optgroup>
                        );
                      }
                      return (
                        <option key={groupLabel} value={groupLabel}>
                          {groupOptions}
                        </option>
                      );
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{`Please provide a valid ${field.title}`}</Form.Control.Feedback>
                  {helpText && (
                    <small className="form-text text-muted">{helpText}</small>
                  )}
                </div>
              );

            default:
              return null;
          }
        })}
      </div>
    );
  };

  const canAddField = (field) => {
    if (field.element?.target_id?.type === "entity_autocomplete") {
      const options = field.element?.target_id?.options || {};
      const realOptions = Object.keys(options).filter(
        (key) => key !== "" && key !== "_none"
      );
      return realOptions.length > 0;
    }
    return true;
  };

  return (
    <>
      {Object.entries(schema).map(([fieldKey, field]) => {
        const label = field.title;
        const isMultiple = field?.cardinality === -1;
        const isCompound = typeof field.element === 'object' && Object.keys(field.element).length > 1;
        const fieldItem = formData[fieldKey];

        return (
          <Form.Group key={fieldKey} className="mb-4">
            <Form.Label>{label}{field.required ? ' *' : ''}</Form.Label>

            {isMultiple ? (
              <>
                {(formData[fieldKey] && formData[fieldKey].length > 0
                  ? formData[fieldKey]
                  : [isCompound ? {} : '']
                ).map((_, idx, arr) => (
                  <div key={idx} className="mb-4">
                    {isCompound
                      ? renderCompoundField(field, fieldKey, idx)
                      : renderSimpleField(field, fieldKey, idx)}
                    <div className="mt-2">
                      {canAddField(field) &&
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeField(fieldKey, idx)}
                          disabled={arr.length === 1}
                        >
                          Remove
                        </Button>
                    }
                    </div>
                  </div>
                ))}
                {canAddField(field) &&
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => addField(fieldKey)}
                  >
                    Add another
                  </Button>
                }
              </>
            ) : (
              isCompound
                ? renderCompoundField(field, fieldKey)
                : renderSimpleField(field, fieldKey)
            )}
          </Form.Group>
        );
      })}
    </>
  );
};

export default DynamicForm;